import { any, every, get, sortBy } from 'lodash/fp';
import moment from 'moment';
import {
    FeedbackNominationByRevieweeProcess,
    FeedbackNominationProcess,
    FeedbackProgress,
    FeedbackType,
} from '../../../../../types/Feedback';
import MiniUser from '../../../../../types/MiniUser';
import { assertNever } from '../../../../../types/helpers';
import colours from '../../../../colours';
import { isFeatureEnabled } from '../../../services/Features';
import replacement from '../../../services/Replacement';
import { CycleFeedbackDto, FeedbackDetailsDto, FeedbackDto } from '../types';

export const getNoteWithAuthor = (feedback: FeedbackDto, allowNoteFromReviewee = true) => {
    switch (feedback.type) {
        case 'REQUESTED':
            return { note: feedback.noteFromRequester || '', author: feedback.requester };
        case 'CYCLE':
            if (
                allowNoteFromReviewee &&
                !feedback.noteFromHr &&
                feedback.noteFromReviewee &&
                feedback.reviewees.length === 1
            ) {
                return { note: feedback.noteFromReviewee, author: feedback.reviewees[0] };
            }
            return { note: feedback.noteFromHr || '' };
        default:
            return assertNever(feedback);
    }
};

export const getSecondaryNote = (feedback: FeedbackDto) => {
    switch (feedback.type) {
        case 'CYCLE':
            return feedback.noteFromReviewee || '';
        default:
            return '';
    }
};

export const getReleaseDate = (feedback: FeedbackDto) => {
    switch (feedback.type) {
        case 'REQUESTED': // if true, its released immediately
            return feedback.releasedToReviewee ? undefined : feedback.deadline;
        case 'CYCLE':
            return feedback.releaseToYouBy ? feedback.releaseToYouBy : undefined;
        default:
            return assertNever(feedback);
    }
};

export function isCycleFeedbackArray(array: FeedbackDto[]): array is CycleFeedbackDto[] {
    return every(feedback => feedback.type === 'CYCLE', array);
}

export const aggregateFeedbackByReviewee = (initial: CycleFeedbackDto, rest: CycleFeedbackDto[]): FeedbackDto => {
    const list = [initial, ...rest];
    const reviewees = list.flatMap(f => f.reviewees);
    const canViewDetails = any(get('canViewDetails'), list);

    return {
        ...initial,
        canViewDetails,
        reviewees: reviewees,
        canNominateReviewers: false,
        noteFromReviewee: '',
    };
};

export const getTypeLabel = (type: FeedbackType): string => {
    switch (type) {
        case 'CYCLE':
            return replacement('fr.singular.title');
        case 'REQUESTED':
            return isFeatureEnabled('FEATURE_anytimeFeedbackGive') ? replacement('anyfb.name') : 'Feedback Request';
        default:
            return assertNever(type);
    }
};

export const getCreatedYear = (feedback: FeedbackDto) => moment(feedback.createdAt).year();

export const getEndDate = (feedback: FeedbackDto) => {
    switch (feedback.type) {
        case 'REQUESTED':
            return feedback.deadline;
        case 'CYCLE':
            return feedback.cycleEndsAt;
        default:
            return assertNever(feedback);
    }
};

export const getTypeName = (feedback: FeedbackDto) => {
    switch (feedback.type) {
        case 'REQUESTED':
            return isFeatureEnabled('FEATURE_anytimeFeedbackGive') ? replacement('anyfb.name') : 'Feedback Request';
        case 'CYCLE':
            return `${replacement('fr.review.singular.title')} Cycle`;
        default:
            return assertNever(feedback);
    }
};

export const feedbackWillNotChangeFurther = ({ status: s }: FeedbackDto) =>
    s === 'COMPLETED' || s === 'CYCLE_ENDED' || s === 'DEADLINE_PASSED';

export const getReviewersSortedByProgress = (feedback: FeedbackDto) =>
    sortBy(reviewer => {
        const progress = feedback.progressByReviewer[reviewer.id];
        switch (progress) {
            case undefined:
                return 0;
            case 'NOT_STARTED':
                return 0;
            case 'WRITING':
                return 1;
            case 'DECLINED':
                return 2;
            case 'HIDDEN':
                return 3;
            case 'WRITTEN':
                return 4;
            default:
                return assertNever(progress);
        }
    }, feedback.reviewers);

export const isReviewee = (currentUserId: string, feedback: FeedbackDto) => {
    return any((reviewee: MiniUser) => reviewee.id === currentUserId, feedback.reviewees);
};

export const isReviewer = (currentUserId: string, feedback: FeedbackDto) => {
    return any((reviewer: MiniUser) => reviewer.id === currentUserId, feedback.reviewers);
};

export const getReviewees = (feedbackDetails: FeedbackDetailsDto[]) => feedbackDetails.flatMap(f => f.reviewees);

export const showGeneralFeedbackWording =
    isFeatureEnabled('requestFeedback') &&
    (!isFeatureEnabled('feedback') || isFeatureEnabled('FEATURE_anytimeFeedbackGive'));

export const getProgressLabel = (progress: FeedbackProgress): string => {
    switch (progress) {
        case 'WRITING':
            return 'Started';
        case 'NOT_STARTED':
            return 'Not started yet';
        case 'DECLINED':
            return 'Declined';
        case 'HIDDEN':
            return 'Unknown';
        case 'WRITTEN':
            return 'Done';
    }
};

export const getProgressColor = (progress: FeedbackProgress): string => {
    switch (progress) {
        case 'WRITING':
            return colours.SIDataYellow;
        case 'NOT_STARTED':
            return colours.SIDataBadred;
        case 'DECLINED':
            return colours.SIDataOrange;
        case 'HIDDEN':
            return colours.SIGray2;
        case 'WRITTEN':
            return colours.SIDataGoodgreen;
    }
};

export const isByRevieweeNominationProcess = (
    nominationProcess: FeedbackNominationProcess
): nominationProcess is FeedbackNominationByRevieweeProcess => {
    switch (nominationProcess) {
        case 'REVIEWEE':
        case 'REVIEWEE_WITHOUT_APPROVAL':
            return true;
        default:
            return false;
    }
};

export const shouldShowSuggestedObjectives = (details: FeedbackDetailsDto | FeedbackDto) => {
    const encourageObjectives =
        isFeatureEnabled('ai-features') &&
        (details.type === 'CYCLE'
            ? isFeatureEnabled('FEATURE_encourageObjectives')
            : isFeatureEnabled('FEATURE_anytimeFeedbackEncourageObjectives'));
    return hasFeedback(details) && encourageObjectives;
};

export const hasFeedback = (feedback: FeedbackDetailsDto | FeedbackDto) => {
    return isFeedbackDetailsDto(feedback)
        ? feedback.questionsWithAnswers.flatMap(x => x.answers).length > 0
        : feedback.hasFeedback && feedback.releasedToReviewee;
};

function isFeedbackDetailsDto(feedback: FeedbackDetailsDto | FeedbackDto): feedback is FeedbackDetailsDto {
    return (feedback as FeedbackDetailsDto).questionsWithAnswers !== undefined;
}
